/**
 * API contracts for the `replenishment` module (M3 second half, spec §5.5).
 *
 * Replenishment has NO tables of its own (DATA-MODEL "Replenishment (v1)"): the
 * suggestion list is computed on demand from stock, open sales-order demand,
 * open purchase-order coverage and 28 days of consumption out of
 * `stock_movements`. The only write is "create draft PO", which produces
 * ordinary purchase orders through the receiving module's `po.create` command.
 *
 * OWNERSHIP: the receiving/replenishment module agent owns this file exclusively.
 * Import from '@warehouse/shared'.
 */
import { z } from 'zod';

import { qtySchema, uuidSchema } from '../schemas/common.js';

/** Why an article is on the work list. Several can apply at once. */
export const replenishmentReasonSchema = z.enum([
  /** available + on order is below `articles.reorder_point_base` */
  'below_reorder_point',
  /** available + on order is below `articles.min_stock_base` */
  'below_min_stock',
  /** days of cover < lead time + safety days */
  'days_cover',
]);
export type ReplenishmentReason = z.infer<typeof replenishmentReasonSchema>;

/**
 * One row of the work list. Every number is a base-unit decimal string except
 * `daysCover` (a float, `null` when the article has no measurable consumption)
 * and the day counts.
 */
export interface ReplenishmentSuggestion {
  articleId: string;
  articleNo: string;
  articleName: string;
  categoryId: string | null;
  categoryName: string | null;
  supplierId: string | null;
  supplierName: string | null;
  baseUnitCode: string;

  /** Physical stock across every bin. */
  onHandBase: string;
  /** Unpicked quantity on open sales orders. */
  demandBase: string;
  /** Outstanding quantity on open purchase orders. */
  onOrderBase: string;
  /** `onHand - demand` — what is genuinely free. */
  availableBase: string;
  /** Average daily consumption over the configured window. */
  avgDailyBase: string;
  /** `(available + onOrder) / avgDaily`, null when nothing moves. */
  daysCover: number | null;

  leadTimeDays: number;
  safetyDays: number;
  reorderPointBase: string | null;
  minStockBase: string | null;
  maxStockBase: string | null;

  /** How much to buy to reach the target level. `'0.000'` when nothing is needed. */
  suggestedQtyBase: string;
  /** True when at least one reason fired. */
  suggest: boolean;
  /**
   * Whether "create draft PO" can act on this row: false when the article has
   * no default supplier (BC's item master carries no vendor, so `supplier_id`
   * is a WMS-side field to set on the article). The UI must not offer the draft
   * action when this is false — it would fail server-side with no orderable lines.
   */
  canDraftPo: boolean;
  reasons: ReplenishmentReason[];
}

/**
 * The knobs behind the calculation, echoed with every list so the UI can
 * explain a suggestion instead of just asserting it (`app_settings`:
 * `replenishment_safety_days`, `replenishment_consumption_window_days`).
 */
export interface ReplenishmentSettings {
  safetyDays: number;
  consumptionWindowDays: number;
  /** Fallback when `articles.lead_time_days` is null. */
  defaultLeadTimeDays: number;
}

export interface ReplenishmentListMeta {
  settings: ReplenishmentSettings;
  /** Number of rows in the whole (unpaged) result where `suggest` is true. */
  suggestedCount: number;
}

export const replenishmentDraftPoLineSchema = z.object({
  articleId: uuidSchema,
  qtyBase: qtySchema,
  /** Overrides the article's default supplier when the buyer picks another. */
  supplierId: uuidSchema.nullish(),
});
export type ReplenishmentDraftPoLine = z.infer<typeof replenishmentDraftPoLineSchema>;

/**
 * Create draft purchase orders from selected suggestions. Lines are grouped by
 * supplier — one PO per supplier, which is what a buyer actually sends.
 */
export const replenishmentDraftPoSchema = z.object({
  lines: z.array(replenishmentDraftPoLineSchema).min(1).max(500),
  expectedDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD')
    .nullish(),
  note: z.string().trim().max(1000).nullish(),
});
export type ReplenishmentDraftPoInput = z.infer<typeof replenishmentDraftPoSchema>;

export interface ReplenishmentDraftPoResult {
  orders: {
    poId: string;
    poNo: string;
    supplierId: string;
    supplierName: string;
    lineCount: number;
  }[];
  /** Articles skipped because they have no supplier at all. */
  skippedArticleIds: string[];
}

/** Payload of the nightly `replenishment.snapshot` event (trend data, no table). */
export interface ReplenishmentSnapshotEvent {
  at: string;
  suggestedCount: number;
  settings: ReplenishmentSettings;
}
